import { captureException } from '@sentry/react'
import type { Extras } from '@sentry/types/build/types/extra'
import { Data, getBodyContentType, Method, toBody, toExtraJson, toFormData } from 'util/api'
import { fetchRetry } from 'util/fetch'
import { parseJson } from 'util/json'
import { omit, omitUndefined } from 'util/object'

export type ErrorJson = {
  message?: string
}

export class HttpError extends Error {
  readonly status: number
  readonly json: ErrorJson

  static async fromResponse(response: Response, method: string, path: string) {
    const { status } = response
    const desc = `${status} ${method} ${path}`
    const text = await response.text().catch(() => '')
    return new HttpError(status, desc, text)
  }

  constructor(status: number, desc: string, text?: string) {
    const json: ErrorJson = parseJson(text) ?? omit({ message: text })
    const message = [desc, json.message].filter(s => s).join(' - ')
    super(message)
    this.status = status
    this.json = json
  }
}

export type ExtractResponse<T> = (response: Response) => Promise<T>
export type Tune<T = unknown> = {
  extend?: false
  extract?: ExtractResponse<T>
}

class Rest {
  client?: string
  company?: string
  access?: string
  origin?: string

  setup(client: string, company: string, access: string, origin: string) {
    rest.client = client
    rest.company = company
    rest.access = access
    rest.origin = origin
  }

  get<T>(path: string, data?: Data, tune?: Tune<T>): Promise<T> {
    return this.search('GET', path, data, tune)
  }

  del<T>(path: string, data?: Data, tune?: Tune<T>): Promise<T> {
    return this.search('DELETE', path, data, tune)
  }

  post<T>(path: string, data?: Data, tune?: Tune<T>): Promise<T> {
    return this.send('POST', path, data, tune)
  }

  put<T>(path: string, data?: Data, tune?: Tune<T>): Promise<T> {
    return this.send('PUT', path, data, tune)
  }

  patch<T>(path: string, data?: Data, tune?: Tune<T>): Promise<T> {
    return this.send('PATCH', path, data, tune)
  }

  form<T>(path: string, data: Data): Promise<T> {
    const body = toFormData(omitUndefined(data))
    return this.fetch('POST', path, { body })
  }

  private search<T>(method: Method, path: string, data?: Data, tune?: Tune<T>): Promise<T> {
    data = this.extend(data, tune)

    const split = path.split('?')
    const base = split[0]
    const params = new URLSearchParams(split[1])

    if (data) {
      for (const key of Object.keys(data)) {
        const value = data[key]
        if (value != null) params.append(key, value.toString())
      }
    }

    const search = params.size === 0 ? '' : '?' + params.toString()
    return this.fetch(method, base + search, {}, tune)
  }

  private send<T>(method: Method, path: string, data?: Data, tune?: Tune<T>): Promise<T> {
    data = this.extend(data, tune)

    const body = toBody(data)
    const headers = omit({
      'Content-Type': getBodyContentType(body),
    })
    return this.fetch(method, path, { body, headers }, tune)
  }

  private extend(data?: Data, tune?: Tune): Data {
    data = data ?? {}
    if (tune?.extend === false) return omitUndefined(data)

    data['client'] = this.client || undefined
    data['company'] = this.company || undefined
    data['access'] = this.access || undefined
    data['origin'] = this.origin || undefined
    return omitUndefined(data)
  }

  private async fetch<T>(method: Method, path: string, options: RequestInit, tune?: Tune<T>): Promise<T> {
    try {
      const response = await fetchRetry(path, { method, ...options })
      if (!response.ok) throw await HttpError.fromResponse(response, method, path)
      if (tune?.extract) return await tune.extract(response)
      return await response.json()
    } catch (e) {
      const skip = e instanceof HttpError && e.status < 500
      if (!skip) {
        const extra = this.extra(method, path, options)
        captureException(e, { extra })
      }
      throw e
    }
  }

  private extra(method: Method, fullPath: string, options: RequestInit | undefined): Extras {
    const url = new URL(fullPath, location.href)
    const path = url.pathname
    const query = Object.fromEntries(url.searchParams.entries())
    const body = toExtraJson(options?.body)
    return { request: { method, path, body, query } }
  }
}

export const rest = new Rest()
